import { AppMode, Entity, ParsedTwin, Roles, Template } from '@repo/frontend-types';
import { useAuth0 } from '@auth0/auth0-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

// Context
import {
  UiSettingsStore,
  useUiSettingsStore,
  useNotificationStore,
  useUserStore,
  useSettingsStore,
  useSpotlightStore,
  useTwinContext,
  useModeContext,
} from '@repo/front-end-state';

// Templates
import { gymTwinTemplate, expoTwinTemplate, eventTwinTemplate } from '@repo/front-end-data';

// Hooks
import { useQueryParams, visuallyHidden } from '@repo/ui';

// Data

// Stores
import { useTwinConfigStore, useEquipmentClassificationStore } from '@repo/front-end-state';

// Utils
import Config from '../../../common/Config';
import { parseTwin } from '../../../common/utils/parseTwin';
import { getCookie } from '@repo/front-end-utils';
import { twinBelongsToOrg } from '@repo/front-end-utils';

// Hooks
import { useSpotlight } from '../../../common/hooks/useSpotlight';

// Components
import {
  ErrorBoundary,
  Frame,
  HeatmapTuningPanel,
  OnboardingWrapper,
  modelManager,
} from '@repo/ui';

// Local version (original):
import { MapCanvas } from '../../../components/3D/MapCanvas';

// Shared version (from @repo/ui):
// import { MapCanvas } from '@repo/ui';

// App-specific components passed as render props to Frame
import Editor from '../../../components/Editor/Editor';
import { rolesKnownFor, useEditModeEntry } from './useEditModeEntry';
import { editTwinTarget, unparsedLoadOutcome, useSendToOnboarding } from './useSendToOnboarding';
import { useSceneCrash } from './useSceneCrash';
import { useEditFlight } from './useEditFlight';
import DeveloperConsole from '../../../components/Admin/DeveloperConsole/DeveloperConsole';
import OrganisationSelector from '../../../components/Admin/OrganisationSelector/OrganisationSelector';
import { useOpsEntityActions, useOpsHostValues } from '../../../components/OpsDashboard';

import {
  getTwinById,
  getMetrics,
  dgqFetchTwin,
  getTwinConfig,
  getEquipmentClassifications,
  createMiddlewareDataProvider,
  beginLoad,
} from '@repo/front-end-services';
import {
  EQUIPMENT_PHASES,
  beginEquipmentLoad,
  markEnd,
  markStart,
  noteEquipmentDataArrived,
} from '@repo/front-end-utils';
// Chart-backed native-dashboard panels (equipment-overview). Imported from the
// @repo/ui/charts subtree (not the main barrel) so chart.js is paid for only by
// the app that renders dashboards.
import { CHART_PANEL_COMPONENTS } from '@repo/ui/charts';
// Twin page layout (was Tailwind `relative` and `w-full py-8 relative`).
const TwinPageContainer = styled.div`
  position: relative;
`;

const TwinPageContent = styled.div`
  width: 100%;
  padding-top: 2rem;
  padding-bottom: 2rem;
  position: relative;
`;

interface Props {
  className?: string;
  mapLib?: any;
}

const storeSelector = (store: UiSettingsStore) => ({
  directedPlaceholderGeometry: store.directedPlaceholderGeometry,
});

// --- TEMP DEBUG: inspect the dGraph model used to drive the 3D scene ----------
// Added to diagnose plan-activation models not rendering. Gated to non-production
// so it never logs (or leaks model details) in deployed builds. Delete this block
// + its call sites tagged [TWIN-DEBUG] once resolved.
const DEBUG_TWIN_MODEL = process.env.NODE_ENV !== 'production';

// Compact recursive view of an entity tree: per node, the identity + child count
// + which 3D-relevant fields are present. Makes it obvious where a
// plan-activated model differs from an FML-imported one that renders.
const summariseEntity = (node: any, depth = 0): any => {
  if (!node) return node;
  return {
    bID: node.bID,
    name: node.name,
    type: node.type?.name ?? node.type,
    childCount: Array.isArray(node.children) ? node.children.length : 0,
    has: {
      position: node.position != null,
      rotationY: node.rotationY != null,
      mapOrigin: node.mapOrigin != null,
      altitude: node.altitude != null,
      modelFile: node.modelFile != null,
      definitions: node.definitions != null,
      geometry: node.geometry != null || node.cfg != null,
      coordinateSystem: node.coordinateSystem != null,
    },
    children:
      depth < 5 && Array.isArray(node.children)
        ? node.children.map((c: any) => summariseEntity(c, depth + 1))
        : undefined,
  };
};

/** A twin with only its record and an empty site: what the page holds when the model is missing
 * or unreadable, so the record is in context for the Edit panel. */
const basicTwinFor = (adminTwin: ParsedTwin['adminTwin']): ParsedTwin => {
  const basicEntity: Entity = {
    id: '',
    name: adminTwin.name || 'Twin',
    type: {
      id: '',
      name: 'SITE',
      __typename: 'TwinEntityType',
    },
    bID: adminTwin.bid,
    children: [],
  };
  return { physicalModel: basicEntity, adminTwin, filterModel: null, totalCapacity: 0 };
};

export const TwinPage: React.FC<Props> = ({ className, mapLib }) => {
  const { isAuthenticated, getAccessTokenSilently, isLoading: auth0IsLoading } = useAuth0();
  const { settings, setMetrics, setSettings } = useSettingsStore();
  const { appMode, setAppModeWithFilterReset } = useModeContext();

  const { twinBId, setTwin, twin, twinNeedsRefresh, setTwinNeedsRefresh, mapRef } =
    useTwinContext();
  const { authMetadata } = useUserStore();
  const { heroMetrics, setTwinConfig } = useTwinConfigStore();
  const {
    fetchedForTwinBid: equipmentClassificationsFetchedFor,
    retryNonce: equipmentClassificationsRetryNonce,
    setClassifications: setEquipmentClassifications,
    setLoading: setEquipmentClassificationsLoading,
    setError: setEquipmentClassificationsError,
    clearEquipmentClassifications,
  } = useEquipmentClassificationStore();
  // Twin whose classification fetch is currently in flight — dedupes a concurrent second fetch
  // (e.g. the clear-on-switch store reset re-triggering the effect, or a token refresh mid-flight).
  const equipmentFetchInFlightForRef = useRef<string | null>(null);

  const navigate = useNavigate();
  const queryParams = useQueryParams();

  const { directedPlaceholderGeometry } = useUiSettingsStore(storeSelector);

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [allModelsLoaded, setAllModelsLoaded] = useState(false);
  /**
   * The twin this page visit's own load last finished for — success or failure — reset whenever the
   * twin changes. `?edit=` waits on it rather than on the 3D scene, which can report "loaded" from a
   * twin left in the store by an earlier visit before this visit's load has set Live
   * (`twin-details-page-position-placeholder`, review).
   */
  const [loadSettledFor, setLoadSettledFor] = useState<string | null>(null);
  const [showTutorial, setShowTutorial] = useState<boolean>(false);
  const [restartTutorial, setRestartTutorial] = useState<boolean>(false);

  const prevDirectedPlaceholderGeometry = useRef(directedPlaceholderGeometry);

  // Check authentication status early and redirect if not authenticated
  // This prevents showing loading messages to logged-out users
  useEffect(() => {
    // Wait for Auth0 to finish loading
    if (auth0IsLoading) {
      return;
    }

    // Redirect immediately if not authenticated (before showing loading messages)
    // Check if not explicitly authenticated (handles false, undefined, null)
    if (!isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }
  }, [isAuthenticated, auth0IsLoading, navigate]);

  // Navigation guards - set appropriate app mode based on available data
  // This runs after authentication check to ensure user is logged in
  useEffect(() => {
    // Wait for authentication and authMetadata to be available
    if (!isAuthenticated || !authMetadata) {
      return;
    }

    // If orgBID is missing, switch to ORG_SELECTION mode
    if (!authMetadata.orgBID) {
      setAppModeWithFilterReset(AppMode.ORG_SELECTION);
      return;
    }

    // If no twin has been selected, switch to TWIN_SELECTION mode
    if (!twinBId) {
      setAppModeWithFilterReset(AppMode.TWIN_SELECTION);
      return;
    }

    // All required data is available - ready for twin loading
  }, [isAuthenticated, authMetadata, twinBId, setAppModeWithFilterReset]);

  // Live middleware-backed provider for native dashboards (equipment-overview FR4.3).
  // Injected into Frame → BIDashboard → NativeDashboardView; undefined until the token is
  // available (NativeDashboardView falls back to the fixture provider until then).
  // `organisation` is still passed, but at query-service 2.0.0 the baseline reads no longer need it:
  // they go through the org-scoped dashboard-data routes, which resolve the organisation SERVER-side
  // from the caller's token and never accept it from the browser. It is retained for the provider
  // legs still on the calendar proxy, which takes it as a query param (its established,
  // guard-validated pattern — the same source the ops dashboard passes to getBaselineView).
  // Dropping it altogether belongs with the ledgered calendar-convergence work, not here.
  const nativeDashboardProvider = useMemo(
    () =>
      accessToken
        ? createMiddlewareDataProvider({
            middlewareApi: Config.middlewareApi,
            accessToken,
            organisation: authMetadata?.organisation,
          })
        : undefined,
    [accessToken, authMetadata?.organisation],
  );

  /**
   * The two HOST CHANNELS the dashboard renderer forwards to every panel component (row 26).
   *
   * Called unconditionally, as hooks must be, and cheap in every mode: `useOpsEntityActions` is a
   * handful of `useCallback`s over context the page already holds (fly-to, hover, and the "Show
   * whole twin" reset), and `useOpsHostValues` is one
   * `useMemo` over the live data context. They are handed to `Frame` regardless of app mode —
   * `BIDashboard` only mounts where the dashboard overlay does, and a component that has no use
   * for either simply ignores them.
   */
  const opsEntityActions = useOpsEntityActions();
  const opsHostValues = useOpsHostValues();

  // Helper function to check if user has TL_ADMIN role
  const isTlAdmin = useCallback(() => {
    return authMetadata?.roles?.includes(Roles.TL_ADMIN) || false;
  }, [authMetadata?.roles]);

  // `?edit=<BID>` from Admin's Open FML link: Edit mode, which is FML's page, once this twin is
  // loaded (`twin-details-page-position-placeholder`, R5; `retire-edit-mode-fml-page`).
  // Roles, not just metadata: the store is restored without roles before Auth0 fills them in.
  const rolesKnown = rolesKnownFor(authMetadata);

  useEditModeEntry({
    loadSettledFor,
    rolesKnown,
    isTlAdmin: isTlAdmin(),
    twinBId,
    twin,
    appMode,
    setAppModeWithFilterReset,
  });

  // A load that finds the twin broken or empty opens onboarding for it, not Edit mode
  // (`retire-edit-mode-entry`, R4-R7). The `?edit=` handshake above is exempt.
  const sendToOnboarding = useSendToOnboarding({ twinBId, rolesKnown, isTlAdmin: isTlAdmin() });

  // The mode switcher's *Edit* flies to Floor Plan's plan view and dissolves into onboarding
  // (`spec-onboarding-persistent-scene-edit-flight`); anything that cannot fly navigates straight
  // away, as before. A change of twin, organisation or mode stops a flight under way (R9).
  const getLiveMap = useCallback(() => mapRef.current, [mapRef]);
  const editFlight = useEditFlight({
    twinBid: twinBId,
    twinName: twin?.adminTwin?.name ?? null,
    accessToken,
    middlewareApi: Config.middlewareApi,
    getMap: getLiveMap,
    watched: [twinBId, appMode, authMetadata?.orgBID],
  });
  const startEditFlight = editFlight.start;
  const cancelEditFlight = editFlight.cancel;
  // R9: any choice in the mode switcher but Edit stops a flight, re-choosing the current mode too.
  const onModeChoice = useCallback(
    (mode: AppMode) => {
      if (mode !== AppMode.EDIT) cancelEditFlight();
    },
    [cancelEditFlight],
  );

  // The mode switcher's *Edit* opens onboarding for the loaded twin, as the wrench does (R1).
  const onEditTwin = useCallback(() => {
    // The option is only offered to TwinLabs admins; anyone else reaching this changes nothing.
    if (!isTlAdmin()) return;
    const target = editTwinTarget({
      isTlAdmin: isTlAdmin(),
      adminTwin: twin?.adminTwin,
      selectedBid: twinBId,
    });
    if (!target) {
      // eslint-disable-next-line no-console
      console.info('[twin] onboarding address unavailable', {
        reason: 'edit',
        twinBid: twin?.adminTwin?.bid ?? null,
      });
      return;
    }
    startEditFlight(target);
  }, [isTlAdmin, twin, twinBId, startEditFlight]);

  const handleRestartTutorial = () => {
    //setAppModeWithFilterReset(AppMode.LIVE);
    setRestartTutorial(true);
  };

  // Org-wide bell (alert-ux FR4): the bell shows alerts across ALL the org's twins, so the fetch
  // is keyed on the selected organisation BID (the same source the org switcher / useSpotlight
  // use: authMetadata.orgBID) rather than the current twin.
  const fetchNotificationsSafely = useCallback(async (token: string, orgBid: string) => {
    try {
      const { fetchOrgNotifications } = useNotificationStore.getState();
      await fetchOrgNotifications(Config.middlewareApi, token, orgBid);
    } catch (error) {
      console.error('TwinPage: Error loading notifications:', error);
      // Don't fail the entire loading process if notifications fail
    }
  }, []);

  // Re-consent legal notices (spec-legal-reconsent-notices FR8). Notices are twin-INDEPENDENT, so
  // this fetches once per page load (keyed on the access token, NOT per-twin) — no polling, in
  // line with the notification system's on-load convention. fetchLegalNotices is fail-open and
  // refetches itself after an acknowledge, so no twinBId dependency is needed here.
  const fetchLegalNoticesSafely = useCallback(async (token: string) => {
    try {
      const { fetchLegalNotices } = useNotificationStore.getState();
      await fetchLegalNotices(Config.middlewareApi, token);
    } catch (error) {
      console.error('TwinPage: Error loading legal notices:', error);
    }
  }, []);

  const onAllModelsLoaded = useCallback(() => {
    setAllModelsLoaded(true);
  }, [setAllModelsLoaded]);

  // A scene crash clears the loading spinner (so the panels still appear) but is not a load: the
  // spotlight is told the scene is loaded only while it has loaded AND not crashed since, so a
  // crash after loading (the map gone) stops any further fly-to.
  const { crashed: sceneCrashed, onCrash: onSceneCrash } = useSceneCrash(twinBId);

  /*
   * UseEffects are triggered when the Main Page loads
   * Fetches Model and Tags for the Twin/Site
   * Passes both these datasets into the TwinParser which preps the data for the 2D and 3D scene
   *
   */
  useEffect(() => {
    const getAccessToken = async () => {
      setAccessToken(await getAccessTokenSilently());
    };
    getAccessToken();
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    // If the twinBID changes we need to reset the AllModelsLoaded
    setAllModelsLoaded(false);
    setLoadSettledFor(null);
    // Drop the previous twin's equipment classifications so a stale twin never leaks through and
    // the lazy Equipment-mode fetch re-runs for the new twin.
    clearEquipmentClassifications();
  }, [twinBId, clearEquipmentClassifications]);

  // Mirror the URL spotlight into the SpotlightStore. fireSpotlight bumps the
  // store's fire-count; useSpotlight (which subscribes to the store directly)
  // reacts and runs its org/twin/entity pipeline. Same dispatch path as the
  // in-app useFlyToBid hook — one canonical write protocol.
  const fireSpotlight = useSpotlightStore(state => state.fireSpotlight);
  const clearSpotlight = useSpotlightStore(state => state.clearSpotlight);
  const prevUrlSpotlightRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const urlSpotlight = queryParams.spotlight;
    if (urlSpotlight === prevUrlSpotlightRef.current) return;
    prevUrlSpotlightRef.current = urlSpotlight;
    // Trim before branching so a whitespace-only URL value (e.g. `?spotlight=%20`)
    // clears the spotlight rather than no-opping inside fireSpotlight and
    // leaving a stale BID active.
    if (urlSpotlight?.trim()) {
      fireSpotlight(urlSpotlight);
    } else {
      clearSpotlight();
    }
  }, [queryParams.spotlight, fireSpotlight, clearSpotlight]);

  useSpotlight({
    allModelsLoaded: allModelsLoaded && !sceneCrashed,
    loadingBanterRunning: false,
  });

  useEffect(() => {
    // Check if user has seen the onboarding tutorial
    const hasSeenTutorial = getCookie('hasSeenTutorial');

    if (restartTutorial) {
      setRestartTutorial(false);
      setShowTutorial(true);
    } else if (!restartTutorial && hasSeenTutorial === 'true' && !showTutorial) {
      // Only hide if it's not already shown through restart
      setShowTutorial(false);
    } else if (!hasSeenTutorial || hasSeenTutorial === 'false') {
      // For first-time users
      setShowTutorial(true);
    }
  }, [restartTutorial, showTutorial]);

  // Load twin configuration on page refresh when twinBId is restored but heroMetrics are missing
  // This ensures the metric switcher and other config-dependent UI elements appear
  useEffect(() => {
    const loadTwinConfigOnRefresh = async () => {
      // Only load if we have twinBId and accessToken but no heroMetrics
      if (twinBId && accessToken && heroMetrics.length === 0) {
        try {
          const twinConfig = await getTwinConfig(Config.middlewareApi, accessToken, twinBId);
          if (twinConfig) {
            setTwinConfig(twinConfig);
          }
        } catch (error) {
          console.error('Failed to load twin configuration on refresh:', error);
        }
      }
    };
    loadTwinConfigOnRefresh();
    // Note: Using heroMetrics directly instead of heroMetrics.length to properly track array changes
  }, [twinBId, accessToken, heroMetrics, setTwinConfig]);

  // Lazily load equipment classifications the first time the user enters Equipment mode for a twin
  // (the dataset is dead weight for every non-TL_ADMIN user until GA, so we don't fetch on twin
  // load). Cleared on twin switch above, so this re-fires per twin. Soft-fails: a failed read
  // records an error but never takes the mode down — the panel degrades to "unclassified".
  // `equipmentClassificationsRetryNonce` is a dep so the retry affordance (FR7) re-runs this effect
  // after a soft-fail without leaving Equipment mode.
  // Open a correlated Equipment-mode load (equipment-performance R3). From here on every outbound
  // request carries `x-request-id: equipment-<loadId>-<seq>`, so one Loki query for the loadId
  // returns every request of this load — across the twin config, the DGraph model, the
  // classifications read and the baseline read, which are issued from four different places.
  //
  // MUST BE DECLARED BEFORE the classifications effect below: React runs a component's effects in
  // declaration order, so this is what guarantees the fetch is stamped with the NEW load id rather
  // than the previous load's. Re-runs on twin switch — a different twin is a different load.
  useEffect(() => {
    if (appMode !== AppMode.EQUIPMENT || !twinBId) return;
    beginLoad('equipment');

    // Open the phase marks for this load (equipment-performance R2). `beginEquipmentLoad` advances the
    // load token, resets the envelope bookkeeping, AND clears every phase's stale start marks — one
    // call rather than three things to remember at the call site.
    beginEquipmentLoad();
    // ONLY the total starts here. `panel-render` and `scene-render` start when the data they render
    // actually lands (`noteEquipmentDataArrived`), per R2's "data in store → painted". Starting them
    // at mode entry made both meaningless — see the note in `perf/equipmentLoad.ts`.
    markStart(EQUIPMENT_PHASES.total);
  }, [appMode, twinBId]);

  useEffect(() => {
    if (appMode !== AppMode.EQUIPMENT || !twinBId || !accessToken) return;
    if (equipmentClassificationsFetchedFor === twinBId) {
      // WARM path: no fetch, but the data IS in the store — so report it, or the render phases would
      // never open and a warm load would emit no `panel-render`, `scene-render` or `total` at all.
      noteEquipmentDataArrived('classifications');
      return;
    }
    if (equipmentFetchInFlightForRef.current === twinBId) return; // already fetching this twin
    equipmentFetchInFlightForRef.current = twinBId;

    let cancelled = false;
    const loadEquipmentClassifications = async () => {
      setEquipmentClassificationsLoading(true);
      // `equipment:fetch:classifications` — one of the TWO independent Equipment fetches. The other
      // (the dateless baseline read) is timed in DataContext. They are measured separately because a
      // single `equipment:fetch` could not say which of them is slow, which is the first question.
      markStart(EQUIPMENT_PHASES.fetchClassifications);
      try {
        const data = await getEquipmentClassifications(Config.middlewareApi, accessToken, twinBId);
        // The cancelled guard MUST come before `markEnd` (Copilot on #1145). A twin switch mid-fetch
        // cancels this instance, and React runs cleanup before the new effects — so by the time a
        // stale request resolves, the next load has already called `beginEquipmentLoad()` (clearing
        // this phase) and `markStart` again. Closing the mark here would then do two wrong things at
        // once: emit a bogus measure spanning the NEW load's start to this stale resolution, and —
        // because `markEnd` consumes its start mark — leave the new load's own `markEnd` with nothing
        // to close, silently losing the real measurement. The `finally` below already reasons about
        // exactly this stale-fetch race; the mark simply was not given the same protection.
        if (cancelled) return;
        markEnd(EQUIPMENT_PHASES.fetchClassifications);
        if (data) {
          // `equipment:parse` — response received to data in the store. Answers "are we
          // deserialising something huge?".
          markStart(EQUIPMENT_PHASES.parse);
          // Key on the requested twinBId (what the dedup guard compares against), not the
          // response's twinBid — a mismatched response must never cache under the wrong twin.
          setEquipmentClassifications(twinBId, data.equipment);
          markEnd(EQUIPMENT_PHASES.parse);
          noteEquipmentDataArrived('classifications');
        } else {
          // Soft-fail: leave fetchedForTwinBid unset so re-entering the mode retries.
          setEquipmentClassificationsError('Failed to load equipment classifications');
        }
      } finally {
        // Only the fetch that still owns the in-flight slot for this twin clears loading. This
        // resets loading when the user leaves Equipment mode mid-fetch (ref still points at this
        // twin), but a stale fetch that lost the race to a twin switch (ref now points elsewhere)
        // must NOT flip loading and hide the new twin's pending spinner.
        if (equipmentFetchInFlightForRef.current === twinBId) {
          equipmentFetchInFlightForRef.current = null;
          setEquipmentClassificationsLoading(false);
        }
      }
    };
    loadEquipmentClassifications();

    return () => {
      cancelled = true;
    };
  }, [
    appMode,
    twinBId,
    accessToken,
    equipmentClassificationsFetchedFor,
    equipmentClassificationsRetryNonce,
    setEquipmentClassifications,
    setEquipmentClassificationsLoading,
    setEquipmentClassificationsError,
  ]);

  useEffect(() => {
    // Only hide loading screen if no twin is selected - let users manually choose twins
    // TwinContext already handles restoring previously selected twins from localStorage on refresh
    // If no twin selected, hide loading screen to allow manual twin selection
    if (!twinBId) {
      setAllModelsLoaded(true);
    }
  }, [twinBId, setAllModelsLoaded]);

  const fetchAdminTwin = async (accessToken: string, twinBId: string) => {
    const twin = await getTwinById(Config.middlewareApi, accessToken, twinBId);
    return twin;
  };

  const fetchModel = useCallback(
    async (id: string): Promise<any> => {
      try {
        if (!accessToken) {
          throw new Error('Missing access token');
        }

        if (!authMetadata?.orgBID) {
          throw new Error('Missing organisation bID');
        }

        const model = await dgqFetchTwin(
          Config.middlewareApi,
          accessToken,
          authMetadata?.orgBID,
          id,
        );

        if (DEBUG_TWIN_MODEL) {
          // eslint-disable-next-line no-console
          console.log('[TWIN-DEBUG] dgqFetchTwin model output — twinBId=' + id + ':', model);
          try {
            // eslint-disable-next-line no-console
            console.log('[TWIN-DEBUG] dgqFetchTwin model JSON:\n' + JSON.stringify(model, null, 2));
          } catch {
            /* circular — ignore */
          }
        }

        // If model is null (dgraph failed), return an object indicating failure
        return { model };
      } catch (error) {
        console.error('Error:', error);
        // Return an object indicating failure instead of undefined
        return { model: null, error: error };
      }
    },
    [accessToken, authMetadata],
  );

  const loadTwinIntoContext = useCallback(async () => {
    if (!twinBId || !accessToken) {
      throw new Error("Can't loadTwin without twinBId or accessToken");
    }

    // Fetch admin twin and DGraph model in parallel
    const [adminTwin, model] = await Promise.all([
      fetchAdminTwin(accessToken, twinBId),
      fetchModel(twinBId),
    ]);
    const graphModel = model?.model;

    if (DEBUG_TWIN_MODEL) {
      // eslint-disable-next-line no-console
      console.groupCollapsed(`[TWIN-DEBUG] dGraph fetch — twinBId=${twinBId}`);
      // eslint-disable-next-line no-console
      console.log('fetchModel wrapper:', model);
      if (model?.error) console.warn('[TWIN-DEBUG] fetchModel error:', model.error);
      // eslint-disable-next-line no-console
      console.log('graphModel (raw TwinEntity):', graphModel);
      // eslint-disable-next-line no-console
      console.log(
        '[TWIN-DEBUG] graphModel summary:',
        graphModel ? summariseEntity(graphModel) : null,
      );
      // Full JSON so the exact shape inserted by activatePlan can be inspected.
      try {
        // eslint-disable-next-line no-console
        console.log('[TWIN-DEBUG] graphModel JSON:', JSON.stringify(graphModel, null, 2));
      } catch {
        /* circular — ignore */
      }
      // eslint-disable-next-line no-console
      console.groupEnd();
    }

    // Restore settings from adminTwin if settings are missing (e.g., on page refresh).
    // Read FRESH from the store, not from this callback's closure: an overlapping load started before
    // an earlier one restored them would otherwise restore again and set Live a second time — over
    // an Edit mode `?edit=` has applied in between (`twin-details-page-position-placeholder`, review).
    let restoredSettings = null;
    if (
      adminTwin &&
      !useSettingsStore.getState().settings?.organisation &&
      authMetadata?.organisation
    ) {
      const organisationName = authMetadata.organisation;
      const timezone = adminTwin.iana_timezone || 'Europe/London';
      const template = adminTwin.template || Template.Gym;

      const twinPrefs: Partial<typeof gymTwinTemplate> = {
        organisation: organisationName,
        timeZone: timezone,
      };

      switch (template) {
        case Template.Gym:
          restoredSettings = { ...gymTwinTemplate, ...twinPrefs };
          break;
        case Template.Truck:
          restoredSettings = { ...expoTwinTemplate, ...twinPrefs };
          break;
        case Template.Event:
          restoredSettings = { ...eventTwinTemplate, ...twinPrefs };
          break;
        default:
          restoredSettings = { ...gymTwinTemplate, ...twinPrefs };
          break;
      }

      setSettings(restoredSettings);

      // Set mode to LIVE since we're restoring a twin from local storage
      setAppModeWithFilterReset(AppMode.LIVE);
    }

    // Get the current settings (may have been just restored above)
    // Use restoredSettings directly if we just created it, otherwise use settings from context
    const organisationName = authMetadata?.organisation;

    if (graphModel && adminTwin && organisationName) {
      // Use restored settings if we just created them, otherwise use settings from context
      // This ensures we have physicalEntities and filterEntities from the template
      // The store's settings as they are now, not this callback's closure: an overlapping load that
      // found them already restored (above) must still parse the twin rather than bail out and
      // dismiss the loading screen early (`twin-details-page-position-placeholder`, PR review).
      const effectiveSettings = restoredSettings || useSettingsStore.getState().settings;

      if (!effectiveSettings?.organisation || !effectiveSettings?.physicalEntities) {
        // If settings aren't ready, we can't proceed - hide loading overlay and show error state
        setAllModelsLoaded(true);
        return;
      }

      const organisation = effectiveSettings.organisation;
      const physicalEntities = effectiveSettings.physicalEntities || [];
      const filterEntities = effectiveSettings.filterEntities || [];

      // A model parseTwin cannot read (it throws on some malformed shapes) is a failed parse, so
      // it takes the same recovery as one that returns nothing.
      let result: ReturnType<typeof parseTwin> | null = null;
      try {
        result = parseTwin({
          organisation,
          twinEntity: graphModel,
          adminTwin,
          physicalEntities: physicalEntities ? physicalEntities : [],
          filterEntities: filterEntities ? filterEntities : [],
          apolloExplorerURI: '',
        });
      } catch (error) {
        console.error('[twin] parse failed', { twinBid: twinBId, error });
      }

      if (DEBUG_TWIN_MODEL) {
        // eslint-disable-next-line no-console
        console.groupCollapsed(`[TWIN-DEBUG] parseTwin result — twinBId=${twinBId}`);
        // eslint-disable-next-line no-console
        console.log('parseTwin returned:', result);
        if (result?.twin?.physicalModel) {
          // eslint-disable-next-line no-console
          console.log(
            '[TWIN-DEBUG] physicalModel summary:',
            summariseEntity(result.twin.physicalModel),
          );
          // eslint-disable-next-line no-console
          console.log(
            '[TWIN-DEBUG] physicalModel children length:',
            result.twin.physicalModel.children?.length ?? 0,
          );
          // eslint-disable-next-line no-console
          console.log('[TWIN-DEBUG] multifloor:', result.multifloor);
        } else {
          console.warn('[TWIN-DEBUG] parseTwin returned no usable physicalModel');
        }
        // eslint-disable-next-line no-console
        console.groupEnd();
      }

      if (result) {
        modelManager.initialize({
          twin: result.twin.physicalModel,
          authMetadata,
          directedPlaceholderGeometry,
        });

        setTwin(result.twin, result.multifloor, result.hasFloorLevel);

        if (!result.twin.physicalModel.children?.length) {
          sendToOnboarding(adminTwin, 'empty');
        }

        // If 3D scene is disabled or there are no models, clear loading state immediately
        // Otherwise, the 3D scene's onAllModelsLoaded callback will clear it
        if (Config.disable3DScene === 'true' || !result.twin.physicalModel.children?.length) {
          setAllModelsLoaded(true);
        }
      } else {
        // The record in context, as for a twin with no model, so Admin's Open FML link finds it
        // (`useEditModeEntry` waits for the named twin to be in context).
        setTwin(basicTwinFor(adminTwin), false);
        sendToOnboarding(adminTwin, 'parse-failed');
        setAllModelsLoaded(true);
      }
    } else {
      // If we have adminTwin data but no graphModel (DGraph down), still set the twin
      // so that the edit form can display the data
      if (adminTwin && !graphModel) {
        setTwin(basicTwinFor(adminTwin), false);
      }

      // No model: onboarding is where it gets one. No twin record, or no organisation name, leaves
      // the page as it is, logged. Either way the loading screen goes.
      const outcome = unparsedLoadOutcome({ hasRecord: !!adminTwin, hasModel: !!graphModel });
      if (outcome === 'no-model') {
        sendToOnboarding(adminTwin, 'no-model');
      } else {
        // eslint-disable-next-line no-console
        console.info('[twin] load incomplete', { reason: outcome, twinBid: twinBId });
      }
      setAllModelsLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- also keyed on `settings`, though the load reads them from the store at call time
  }, [
    accessToken,
    authMetadata,
    directedPlaceholderGeometry,
    fetchModel,
    setAppModeWithFilterReset,
    setTwin,
    settings,
    setSettings,
    twinBId,
    setAllModelsLoaded,
    sendToOnboarding,
  ]);

  useEffect(() => {
    const refreshTwin = async () => {
      if (twinNeedsRefresh && twinBId && accessToken) {
        // Verify twin belongs to current org before refreshing
        if (!twinBelongsToOrg(twinBId, authMetadata?.orgBID ?? null)) {
          // Skip refresh if twin doesn't belong to current org
          setTwinNeedsRefresh(false);
          return;
        }
        await loadTwinIntoContext();
        setTwinNeedsRefresh(false);
      } else if (twinNeedsRefresh && (!twinBId || !accessToken)) {
        // If refresh was requested but we don't have required data yet, just clear the flag
        // The main loading effect will handle it once data is available
        setTwinNeedsRefresh(false);
      }
    };
    refreshTwin();
  }, [
    twinNeedsRefresh,
    twinBId,
    accessToken,
    loadTwinIntoContext,
    setTwinNeedsRefresh,
    authMetadata?.orgBID,
  ]);

  useEffect(() => {
    if (twinBId && accessToken && isAuthenticated) {
      // CRITICAL: Prevent loading a twin that belongs to a different organization
      // This race condition can occur when switching organizations via spotlight URL
      // where authMetadata updates before twinBId is cleared/updated
      if (!twinBelongsToOrg(twinBId, authMetadata?.orgBID ?? null)) {
        // Twin doesn't belong to current org, skip loading
        // The spotlight hook will update twinBId to the correct value
        return;
      }

      const fetchMetrics = async (): Promise<undefined | any> => {
        try {
          if (!authMetadata?.orgBID) {
            throw new Error('Missing Organisation bID');
          }
          const metrics = await getMetrics(Config.middlewareApi, accessToken, authMetadata?.orgBID);
          return metrics;
        } catch (error) {
          console.error('Error:', error);
        }
      };

      // Load twin data
      loadTwinIntoContext()
        .then(() => {
          // Twin data is ready, but keep loading screen until models are loaded
          setLoadSettledFor(twinBId);
        })
        .catch(error => {
          console.error('Error loading twin:', error);
          // Only hide loading screen if there's an error and no 3D scene
          setAllModelsLoaded(true);
          setLoadSettledFor(twinBId);
        });

      // e.g. temperature, speed, countEntity, windSpeed
      fetchMetrics().then(data => {
        if (data) {
          setMetrics(data);
        }
      });
    } else if (!twinBId) {
      // If no twin has been selected redirect back to Twin Selector page
      setTwin(null);
    }
  }, [
    settings,
    settings?.entities,
    settings?.pollTwin,
    twinBId,
    allModelsLoaded,
    navigate,
    setMetrics,
    setTwin,
    setAppModeWithFilterReset,
    accessToken,
    isAuthenticated,
    authMetadata,
    directedPlaceholderGeometry,
    loadTwinIntoContext,
    //onSceneReady,
  ]);

  // Fetch notifications org-wide: on load and whenever the selected organisation changes
  // (alert-ux FR4 — no longer keyed on the current twin).
  useEffect(() => {
    if (authMetadata?.orgBID && accessToken && isAuthenticated) {
      fetchNotificationsSafely(accessToken, authMetadata.orgBID);
    }
  }, [authMetadata?.orgBID, accessToken, isAuthenticated, fetchNotificationsSafely]);

  // Fetch legal notices once the token is available (twin-independent — not keyed on twinBId, FR8).
  useEffect(() => {
    if (accessToken && isAuthenticated) {
      fetchLegalNoticesSafely(accessToken);
    }
  }, [accessToken, isAuthenticated, fetchLegalNoticesSafely]);

  useEffect(() => {
    const directedPlaceholderGeometryChanged =
      prevDirectedPlaceholderGeometry.current !== directedPlaceholderGeometry;
    if ((!modelManager.isInitialized() || directedPlaceholderGeometryChanged) && twin) {
      modelManager.reinitialize({
        twin: twin.physicalModel,
        authMetadata,
        directedPlaceholderGeometry,
      });

      if (directedPlaceholderGeometryChanged) {
        prevDirectedPlaceholderGeometry.current = directedPlaceholderGeometry;
      }
    }
  }, [authMetadata, directedPlaceholderGeometry, twin]);

  // Show loading while Auth0 is loading or user is not authenticated
  // This prevents render errors and shows proper loading state before redirect
  // Must be after all hooks to comply with React Hooks rules
  if (auth0IsLoading || !isAuthenticated) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <TwinPageContainer>
        <TwinPageContent id="twin-page">
          {showTutorial && (
            <OnboardingWrapper
              restartTutorial={restartTutorial}
              onComplete={() => setShowTutorial(false)}
            />
          )}
          {Config.disable3DScene === undefined || Config.disable3DScene === 'false' ? (
            <ErrorBoundary onError={onSceneCrash} resetKey={twinBId}>
              <MapCanvas onAllModelsLoaded={onAllModelsLoaded} />
            </ErrorBoundary>
          ) : null}
          {/* Dev-only splat tuning overlay (blob radius / opacity): NODE_ENV-gated + ?heatmapTune=1. */}
          {DEBUG_TWIN_MODEL && queryParams.heatmapTune === '1' && <HeatmapTuningPanel />}
          <Frame
            setShowTutorial={setShowTutorial}
            restartTutorial={handleRestartTutorial}
            allModelsLoaded={allModelsLoaded || sceneCrashed}
            middlewareApi={Config.middlewareApi}
            twinAppBaseUrl={Config.twinAppBaseUrl}
            editorElement={<Editor className="panel" />}
            developerConsoleElement={<DeveloperConsole />}
            organisationSelectorElement={<OrganisationSelector className="panel" />}
            // Create New Twin opens the onboarding wizard's create mode (`twin-details-page-create`).
            onCreateTwin={() => navigate('/onboarding?create=1')}
            // Edit opens onboarding for the loaded twin (`retire-edit-mode-entry`).
            onEditTwin={onEditTwin}
            onModeChoice={onModeChoice}
            dashboardDataProvider={nativeDashboardProvider}
            dashboardPanelComponents={CHART_PANEL_COMPONENTS}
            // HOST CHANNELS (row 26 FR3/FR4). What used to be two whole panel COLUMNS of
            // app-specific React injected as render props is now two small channels: the
            // spotlight/fly-to callbacks, and the live headcount the dGraph subscription
            // publishes. The panels themselves are cells in a definition. Both are app-specific
            // by construction — they bind the viewer's scene and its socket — which is why they
            // are injected here rather than living in @repo/ui.
            dashboardHostActions={opsEntityActions}
            dashboardHostValues={opsHostValues}
            panelsHidden={editFlight.panelsHidden}
          />
          {/* The Edit flight, said (criterion 11.4): the press and a cancel. Focus is never moved. */}
          <span role="status" aria-live="polite" style={visuallyHidden}>
            {editFlight.announcement}
          </span>
        </TwinPageContent>
      </TwinPageContainer>
    </>
  );
};
